// src/components/admin/AdminHeader.tsx
import React, { useState } from 'react';
import { Box, IconButton, Avatar, Typography, Menu, MenuItem, Divider, ListItemIcon } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';

interface AdminHeaderProps {
  onToggleMenu: () => void;
}

const AdminHeader: React.FC<AdminHeaderProps> = ({ onToggleMenu }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  // Get admin info from localStorage
  const userEmail = localStorage.getItem('user_email') || 'admin@example.com';
  const userName = localStorage.getItem('user_name') || 'Admin';
  const userRole = localStorage.getItem('user_role') || 'admin';
  const adminType = localStorage.getItem('admin_type') || userRole;

  // Get first letter of name or email for avatar
  const avatarLetter = userName.charAt(0).toUpperCase() || userEmail.charAt(0).toUpperCase();

  // Function to get display name for admin type
  const getAdminTypeDisplay = (type: string) => {
    switch (type.toLowerCase()) {
      case 'super_admin':
      case 'super admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      case 'operation':
        return 'Operation';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  // Function to get color for admin type badge
  const getAdminTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'super_admin':
      case 'super admin':
        return '#1565c0'; // Blue
      case 'admin':
        return '#6a1b9a'; // Purple
      case 'operation':
        return '#e65100'; // Orange
      default:
        return '#008060'; // Green (default)
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/adminProfile');
  };

  const handleSettings = () => {
    handleClose();
    navigate('/globalSettings');
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_role');
    localStorage.removeItem('admin_type');
    
    handleClose();
    navigate('/adminLogin');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '71px',
        px: 2,
        backgroundColor: '#f7f7f7',
        borderBottom: '1px solid #e1e3e5',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      {/* Left Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={onToggleMenu}
          sx={{
            display: { xs: 'flex', md: 'none' },
            color: '#202223',
            '&:hover': { backgroundColor: '#e4e5e7' },
          }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: '#202223',
            fontSize: '16px',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          Admin Panel
        </Typography>
      </Box>

      {/* Right Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Admin Type Badge */}
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            backgroundColor: getAdminTypeColor(adminType),
            borderRadius: '4px',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'white',
              fontWeight: 600,
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {getAdminTypeDisplay(adminType)}
          </Typography>
        </Box>

        <IconButton
          sx={{
            color: '#202223',
            '&:hover': { backgroundColor: '#e4e5e7' },
          }}
        >
          <SearchIcon fontSize="small" />
        </IconButton>

        <IconButton
          sx={{
            color: '#202223',
            '&:hover': { backgroundColor: '#e4e5e7' },
          }}
        >
          <NotificationsNoneIcon fontSize="small" />
        </IconButton>

        <Avatar
          onClick={handleClick}
          sx={{
            width: 32,
            height: 32,
            backgroundColor: '#008060',
            fontSize: '14px',
            fontWeight: 600,
            ml: 1,
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
        >
          {avatarLetter}
        </Avatar>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              minWidth: 240,
              borderRadius: '8px',
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1.5,
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: '#f6f6f7',
                },
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Profile Info Section */}
          <Box sx={{ px: 2, py: 1.5, cursor: 'default' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                color: '#202223',
                mb: 0.5,
              }}
            >
              {userName}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#6d7175',
                display: 'block',
                mb: 0.5,
              }}
            >
              {userEmail}
            </Typography>
            <Box
              sx={{
                display: 'inline-block',
                px: 1,
                py: 0.25,
                backgroundColor: getAdminTypeColor(adminType),
                borderRadius: '4px',
                mt: 0.5,
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                }}
              >
                {getAdminTypeDisplay(adminType)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Menu Items */}
          <MenuItem onClick={handleProfile}>
            <ListItemIcon>
              <PersonOutlineIcon fontSize="small" sx={{ color: '#202223' }} />
            </ListItemIcon>
            Your Profile
          </MenuItem>

          <MenuItem onClick={handleSettings}>
            <ListItemIcon>
              <SettingsOutlinedIcon fontSize="small" sx={{ color: '#202223' }} />
            </ListItemIcon>
            Settings
          </MenuItem>

          <Divider sx={{ my: 1 }} />

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: '#d72c0d' }} />
            </ListItemIcon>
            <Typography sx={{ color: '#d72c0d', fontWeight: 500 }}>
              Logout
            </Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default AdminHeader;